import React from 'react';
import LegalLayout from './LegalLayout';

export default function DataPolicy() {
    return (
        <LegalLayout title="Data Policy" lastUpdated="April 3, 2026">
            <p>
                This Data Policy explains how NurtureCRM.ai handles, stores, processes, and protects the data you entrust to our platform. It supplements our <a href="/privacy">Privacy Policy</a> with specific details about data management practices.
            </p>

            <h2>1. Data Categories</h2>

            <h3>1.1 Customer Data</h3>
            <p>Data that you and your organization's users enter into or upload to the Service:</p>
            <ul>
                <li>Contacts, accounts, and company records</li>
                <li>Deals and pipeline information</li>
                <li>Activities, notes, and tasks</li>
                <li>Email communications and templates</li>
                <li>Proposals and documents</li>
                <li>Custom fields and tags</li>
                <li>File attachments and imports</li>
            </ul>

            <h3>1.2 Account Data</h3>
            <p>Information related to your NurtureCRM.ai account:</p>
            <ul>
                <li>User profiles and credentials</li>
                <li>Organization settings and configuration</li>
                <li>Subscription and billing information</li>
                <li>Integration connections and tokens</li>
                <li>User roles and permissions</li>
            </ul>

            <h3>1.3 Usage Data</h3>
            <p>Automatically collected data about Service usage:</p>
            <ul>
                <li>Feature usage analytics</li>
                <li>Performance metrics</li>
                <li>Error logs and diagnostics</li>
                <li>Session information</li>
            </ul>

            <h2>2. Data Storage and Infrastructure</h2>

            <h3>2.1 Hosting</h3>
            <ul>
                <li><strong>Cloud Provider:</strong> Amazon Web Services (AWS), region: Asia-Pacific (Singapore, ap-southeast-1)</li>
                <li><strong>Database:</strong> MongoDB Atlas with encryption at rest and in transit</li>
                <li><strong>Static Assets:</strong> AWS CloudFront CDN with S3 origin</li>
                <li><strong>Secrets Management:</strong> AWS Systems Manager Parameter Store with encryption</li>
            </ul>

            <h3>2.2 Encryption</h3>
            <ul>
                <li><strong>In Transit:</strong> All data transmitted between clients and servers is encrypted using TLS 1.2 or higher.</li>
                <li><strong>At Rest:</strong> Database storage is encrypted using AES-256 encryption.</li>
                <li><strong>Credentials:</strong> API keys, tokens, and secrets are stored using encrypted parameter stores, not in application code.</li>
            </ul>

            <h3>2.3 Backups</h3>
            <ul>
                <li>Database backups are performed automatically by MongoDB Atlas.</li>
                <li>Point-in-time recovery is available for the retention period defined by the database tier.</li>
                <li>Backups are encrypted and stored in geographically separate locations.</li>
            </ul>

            <h2>3. Data Isolation and Multi-Tenancy</h2>
            <p>NurtureCRM.ai operates a multi-tenant architecture with logical data isolation:</p>
            <ul>
                <li><strong>Organization Scoping:</strong> All CRM data is scoped to your organization. Users in one organization cannot access data from another organization.</li>
                <li><strong>Role-Based Access:</strong> Within an organization, data access is controlled by user roles (Owner, Admin, Manager, Sales Rep) with granular permissions.</li>
                <li><strong>API Authentication:</strong> All API requests are authenticated via JWT tokens and validated against organization membership.</li>
                <li><strong>Integration Tokens:</strong> Third-party integration tokens (Gmail, Calendar) are stored per-user and are not shared across users or organizations.</li>
            </ul>

            <h2>4. Data Processing</h2>

            <h3>4.1 Purposes</h3>
            <p>Your data is processed for the following purposes:</p>
            <ul>
                <li>Providing CRM functionality (contact management, pipeline tracking, email sequences)</li>
                <li>Delivering AI-powered features (see our <a href="/ai-policy">AI Policy</a>)</li>
                <li>Syncing with connected third-party services (Gmail, Google Calendar)</li>
                <li>Generating reports and analytics within your organization</li>
                <li>Sending transactional emails (notifications, invitations, deal room access)</li>
            </ul>

            <h3>4.2 Sub-Processors</h3>
            <p>We use the following categories of sub-processors:</p>
            <table>
                <thead>
                    <tr><th>Category</th><th>Purpose</th><th>Data Accessed</th></tr>
                </thead>
                <tbody>
                    <tr><td>Cloud Infrastructure (AWS)</td><td>Hosting and compute</td><td>All service data</td></tr>
                    <tr><td>Database (MongoDB Atlas)</td><td>Data storage</td><td>All service data</td></tr>
                    <tr><td>Email Delivery (Resend)</td><td>Transactional emails</td><td>Recipient email, email content</td></tr>
                    <tr><td>AI/LLM Providers</td><td>AI features</td><td>Contextual CRM data per request</td></tr>
                    <tr><td>Payment Processing</td><td>Subscription billing</td><td>Billing information</td></tr>
                </tbody>
            </table>

            <h2>5. Data Retention</h2>
            <table>
                <thead>
                    <tr><th>Data Type</th><th>Retention Period</th></tr>
                </thead>
                <tbody>
                    <tr><td>CRM Data (contacts, deals, accounts)</td><td>Duration of subscription + 90 days</td></tr>
                    <tr><td>User Account Data</td><td>Duration of account + 30 days after deletion</td></tr>
                    <tr><td>Email Sync Data</td><td>Duration of integration connection</td></tr>
                    <tr><td>Audit Logs</td><td>12 months</td></tr>
                    <tr><td>Usage Analytics</td><td>24 months (aggregated)</td></tr>
                    <tr><td>Backups</td><td>Per MongoDB Atlas retention policy</td></tr>
                </tbody>
            </table>

            <h2>6. Data Deletion</h2>

            <h3>6.1 User-Initiated Deletion</h3>
            <ul>
                <li><strong>Individual Records:</strong> Users can delete contacts, deals, accounts, and activities through the Service interface.</li>
                <li><strong>Integration Disconnect:</strong> Disconnecting an integration removes stored tokens and synced data.</li>
                <li><strong>Account Deletion:</strong> Users can request account deletion by contacting support.</li>
            </ul>

            <h3>6.2 Organization-Level Deletion</h3>
            <ul>
                <li>Organization administrators can request full data export before deletion.</li>
                <li>Upon organization deletion, all associated data is permanently removed within 30 days.</li>
                <li>Backups containing deleted data are purged according to the backup retention schedule.</li>
            </ul>

            <h3>6.3 Data Purge</h3>
            <p>Platform administrators have access to data purge capabilities for compliance purposes. Purge operations are logged in the audit trail.</p>

            <h2>7. Data Portability</h2>
            <p>You can export your data from NurtureCRM.ai at any time:</p>
            <ul>
                <li><strong>CSV Export:</strong> Contacts, accounts, deals, and activities can be exported in CSV format.</li>
                <li><strong>API Access:</strong> All data is accessible via our REST API for programmatic export.</li>
                <li><strong>Bulk Export:</strong> Organization administrators can request a full data export by contacting support.</li>
            </ul>

            <h2>8. Data Breach Response</h2>
            <p>In the event of a data breach:</p>
            <ul>
                <li>We will investigate and contain the breach promptly.</li>
                <li>Affected users and organizations will be notified within 72 hours of discovery.</li>
                <li>We will provide details about the nature of the breach, data affected, and remediation steps.</li>
                <li>We will cooperate with relevant authorities as required by law.</li>
                <li>We will conduct a post-incident review and implement measures to prevent recurrence.</li>
            </ul>

            <h2>9. Compliance</h2>
            <p>We design our data practices to align with major data protection frameworks, including:</p>
            <ul>
                <li>General Data Protection Regulation (GDPR)</li>
                <li>California Consumer Privacy Act (CCPA)</li>
                <li>Australian Privacy Principles (APPs)</li>
            </ul>
            <p>For specific compliance inquiries, contact <a href="mailto:support@aicoaches.com">support@aicoaches.com</a>.</p>

            <h2>10. Contact Us</h2>
            <p>For data-related inquiries:</p>
            <ul>
                <li><strong>Email:</strong> <a href="mailto:support@aicoaches.com">support@aicoaches.com</a></li>
                <li><strong>Address:</strong> AiCoaches dot com LLC, 111 NE 1ST ST, 8TH FLOOR 89145, Miami, Florida, USA 33132</li>
            </ul>
        </LegalLayout>
    );
}
